'use client';
import { Box } from '@chakra-ui/react';
import { Suspense, useEffect } from 'react';
import slugify from 'react-slugify';

import AwardAndPeerview from '@/app/_components/award-peerview/AwardAndPeerview';
import Brochure from '@/app/_components/brochure/Brochure';
import FeaturesSlider from '@/app/_components/features-slider/FeaturesSlider';
import { Gallery } from '@/app/_components/gallery/ImageGallery';
import { SECONDARY_FLOATING_BUTTON_THEME } from '@/constants/vars.constants';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { BannerCarousel } from '@/types/banners/banners.types';
import { NewCarData } from '@/types/new-cars/new-cars.types';

import BannerSection from './Banner';
import ColorSection from './ColorSection';
import CompareModels from './CompareModels';
import MatchBanner from './MatchBanner';
import RequestQuote from './RequestQuote';

type ModelPageProps = {
  model: string;
  newVehicle: NewCarData;
  banners: BannerCarousel;
  specials?: {
    id: number;
    range?: {
      id: number;
      name: string;
    };
  }[];
};

const ModelPage = ({
  model,
  newVehicle,
  banners,
  specials,
}: ModelPageProps) => {
  const newModel = model.toString().toLowerCase().replace('kia-', '');
  const exteriorImages =
    newVehicle.gallery_exterior?.map((image) => image.image) ?? [];
  const interiorImages =
    newVehicle.gallery_interior?.map((image) => image.image) ?? [];

  const images = [...exteriorImages, ...interiorImages];

  const { setFormItem, setSecondaryFloatingButton } = useFloatingCTA();

  const promotionUrl =
    specials?.length === 1
      ? `/promotions/${slugify(newVehicle?.range?.name)}/${specials[0]?.id}`
      : `/promotions/${slugify(newVehicle?.range?.name)}`;

  useEffect(() => {
    if (specials && specials.length) {
      setSecondaryFloatingButton({
        url: promotionUrl,
        text: `Promotions`,
        display: 'all',
        colors: {
          ...SECONDARY_FLOATING_BUTTON_THEME,
        },
      });
    }
    setFormItem({
      type: 'new-car',
      newCar: newVehicle,
    });
    return () => {
      setSecondaryFloatingButton(null);
      setFormItem(undefined);
    };
  }, [newVehicle]);

  return (
    <>
      {banners?.mobile_image || banners?.video || banners?.desktop_image ? (
        <BannerSection
          model={newModel}
          newVehicle={newVehicle}
          align={banners?.call_to_action_position}
          banner={banners}
        />
      ) : null}

      <Suspense>
        {newVehicle?.horizontal_features_1 &&
          newVehicle?.horizontal_features_1?.length > 0 && (
            <FeaturesSlider
              content={newVehicle.horizontal_features_1.map(
                (feature, index) => ({
                  ...feature,
                  key: index.toString(),
                }),
              )}
              heading={newVehicle?.horizontal_features_title_1}
              subHeading={newVehicle?.horizontal_features_subtitle_1}
            />
          )}
        <Box className="element" key="gallery">
          {(images || []).length > 0 && (
            <Gallery gallery={images} mainTitle={newVehicle?.main_title} />
          )}
        </Box>
        {newVehicle?.brochure && (
          <Brochure model={newModel} brochure={newVehicle?.brochure} />
        )}
        {newVehicle?.horizontal_features_2 &&
          newVehicle?.horizontal_features_2?.length > 0 && (
            <FeaturesSlider
              content={newVehicle.horizontal_features_2.map(
                (feature, index) => ({
                  ...feature,
                  key: index.toString(),
                }),
              )}
              heading={newVehicle?.horizontal_features_title_2}
              subHeading={newVehicle?.horizontal_features_subtitle_2}
            />
          )}
        {newVehicle?.vertical_features_1 &&
          newVehicle?.vertical_features_1?.length > 0 && (
            <FeaturesSlider
              content={newVehicle.vertical_features_1.map((feature, index) => ({
                ...feature,
                key: index.toString(),
              }))}
              heading={newVehicle?.vertical_features_title_1}
              subHeading={newVehicle?.vertical_features_subtitle_1}
              vertical
            />
          )}
        {newVehicle?.awards && newVehicle?.awards?.length > 0 && (
          <AwardAndPeerview
            heading={'Explore with confidence.'}
            bgColor={'#f8f8f8'}
            awards={newVehicle?.awards}
            peerview={{
              type: newVehicle?.peerview_type || null,
              itemId: newVehicle?.peerview_item_id || null,
            }}
          />
        )}
        {(newVehicle?.colour || []).length > 0 && (
          <ColorSection colour={newVehicle?.colour} />
        )}
        {(newVehicle?.specification_prices || []).length > 0 && (
          <CompareModels
            specificationsPrices={newVehicle?.specification_prices}
            modelName={newModel}
          />
        )}
        {newVehicle?.footer_call_to_actions &&
          newVehicle?.footer_call_to_actions?.length > 0 &&
          newVehicle.footer_call_to_actions[0] && (
            <MatchBanner
              call_to_actions={
                newVehicle.footer_call_to_actions[0].call_to_actions_id
              }
            />
          )}
        {newVehicle?.footer_call_to_actions &&
          newVehicle?.footer_call_to_actions?.length > 1 &&
          newVehicle?.footer_call_to_actions[1] && (
            <RequestQuote
              call_to_actions={
                newVehicle?.footer_call_to_actions[1].call_to_actions_id
              }
            />
          )}
      </Suspense>
    </>
  );
};

export default ModelPage;
